import React, { useEffect, useState } from "react";
import { adminGetInvoiceSettings, adminUpdateInvoiceSettings } from "@/lib/api";

/**
 * The identities invoices are issued under.
 *
 * More than one, because the same person invoices under more than one name —
 * Depmont for studio engagements, a personal freelance identity for work taken
 * directly. Each profile carries its own details and its own number series.
 *
 * Three things behave in ways that are only obvious afterwards, so the page
 * says them out loud: details are copied onto an invoice when it is issued and
 * not re-read after that; the number prefix is what keeps the two runs apart
 * and cannot be shared; and none of this appears anywhere on the public site.
 */

const blankProfile = (n) => ({
    key: "",
    label: `Profile ${n}`,
    number_prefix: "",
    // Empty on purpose. The default card reads "Invoice — Depmont", so a new
    // identity previews with no image until it has one of its own — plainer,
    // but not another business's logo.
    share_card: "",
    legal_name: "",
    trading_name: "",
    address: "",
    email: "",
    phone: "",
    website: "",
    gstin: "",
    pan: "",
    gst_registered: false,
    upi_id: "",
    upi_name: "",
    bank_name: "",
    bank_account_name: "",
    bank_account_number: "",
    bank_ifsc: "",
    bank_branch: "",
    bank_swift: "",
    bank_iban: "",
    default_terms:
        "Payment due within 15 days of the invoice date. Please quote the invoice number with your transfer.",
    default_notes: "",
});

export default function InvoiceSettingsPage() {
    const [settings, setSettings] = useState(null);
    const [active, setActive] = useState(0);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState("");

    useEffect(() => {
        adminGetInvoiceSettings().then((d) => {
            setSettings(d);
            setActive(0);
        });
    }, []);

    const form = settings?.profiles?.[active] || null;

    const set = (k, v) =>
        setSettings((s) => ({
            ...s,
            profiles: s.profiles.map((p, i) => (i === active ? { ...p, [k]: v } : p)),
        }));

    const addProfile = () =>
        setSettings((s) => {
            const next = [...s.profiles, blankProfile(s.profiles.length + 1)];
            setActive(next.length - 1);
            return { ...s, profiles: next };
        });

    const removeProfile = () => {
        if (settings.profiles.length < 2) return;
        if (
            !window.confirm(
                `Remove "${form.label}"?\n\nInvoices already issued under it keep their number and their copy of these details — nothing already sent changes. You just cannot raise new ones under this identity.`,
            )
        )
            return;
        setSettings((s) => {
            const next = s.profiles.filter((_, i) => i !== active);
            const removedKey = s.profiles[active].key;
            setActive(0);
            return {
                ...s,
                profiles: next,
                default_key: s.default_key === removedKey ? next[0].key : s.default_key,
            };
        });
    };

    const makeDefault = () =>
        setSettings((s) => ({ ...s, default_key: s.profiles[active].key }));

    const save = async () => {
        setSaving(true);
        setStatus("");
        try {
            const saved = await adminUpdateInvoiceSettings(settings);
            setSettings(saved);
            setActive((i) => Math.min(i, saved.profiles.length - 1));
            setStatus("Saved.");
        } catch (e) {
            setStatus(
                "Save failed — " + (e?.response?.data?.detail || e?.message || "unknown error"),
            );
        } finally {
            setSaving(false);
        }
    };

    if (!settings || !form) {
        return <p className="eyebrow text-[#1B1B1B]/50">Loading…</p>;
    }

    const isDefault = settings.default_key === form.key && !!form.key;

    return (
        <section data-testid="admin-invoice-settings">
            <header className="border-b border-[#E6E2D6] pb-6 mb-8">
                <p className="eyebrow text-[#1B1B1B]/60 mb-2">§ Invoices — 06</p>
                <h1 className="font-serif-editorial text-4xl md:text-5xl leading-[1.02] tracking-[-0.02em]">
                    Invoice Settings
                </h1>
            </header>

            <div className="font-sans-tight text-sm text-[#1B1B1B]/70 mb-8 max-w-2xl font-light leading-[1.7] space-y-3">
                <p>
                    Each profile is an identity you invoice under. Depmont for studio
                    work, and a separate one for anything you take on directly as a
                    freelancer — different name, different bank details if you want,
                    and its own run of invoice numbers.
                </p>
                <p>
                    Details are copied onto an invoice when you issue it, so changing a
                    bank account here will not alter invoices already sent. None of this
                    appears anywhere on the public website.
                </p>
            </div>

            {/* Profile switcher */}
            <div className="flex flex-wrap items-center gap-2 mb-8 border-b border-[#E6E2D6]">
                {settings.profiles.map((p, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => setActive(i)}
                        data-testid={`profile-tab-${i}`}
                        className={`px-4 py-3 font-sans-tight text-[12px] tracking-[0.18em] uppercase transition-colors duration-300 border-b-2 -mb-px ${
                            i === active
                                ? "border-[#1B1B1B] text-[#1B1B1B]"
                                : "border-transparent text-[#1B1B1B]/50 hover:text-[#1B1B1B]"
                        }`}
                    >
                        {p.label || "Untitled"}
                        {p.number_prefix ? (
                            <span className="text-[#A8844F]"> · {p.number_prefix}</span>
                        ) : null}
                        {settings.default_key === p.key ? " ✓" : ""}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={addProfile}
                    data-testid="profile-add"
                    className="px-4 py-3 eyebrow link-underline text-[#A8844F]"
                >
                    + Add profile
                </button>
            </div>

            <div className="max-w-2xl space-y-10">
                <Group title="This profile">
                    <div className="grid grid-cols-2 gap-6">
                        <Field
                            label="Name in the admin"
                            hint="Only you see this. It labels the tab above and the picker when you raise an invoice."
                        >
                            <input
                                className="editorial-input"
                                value={form.label}
                                onChange={(e) => set("label", e.target.value)}
                                placeholder="Freelance"
                                data-testid="profile-label"
                            />
                        </Field>
                        <Field
                            label="Invoice number prefix"
                            hint="One to four letters. D gives D-2627-001. Each profile needs its own — sharing one would eventually produce the same invoice number twice."
                        >
                            <input
                                className="editorial-input"
                                value={form.number_prefix}
                                maxLength={4}
                                onChange={(e) =>
                                    set(
                                        "number_prefix",
                                        e.target.value.toUpperCase().replace(/[^A-Z]/g, ""),
                                    )
                                }
                                placeholder="AK"
                                data-testid="profile-prefix"
                            />
                        </Field>
                    </div>

                    <div className="flex items-center gap-6 pt-1">
                        {isDefault ? (
                            <p className="eyebrow text-[#A8844F]">
                                Default — new invoices start here
                            </p>
                        ) : (
                            <button
                                type="button"
                                onClick={makeDefault}
                                className="eyebrow link-underline"
                            >
                                Make this the default
                            </button>
                        )}
                        {settings.profiles.length > 1 && (
                            <button
                                type="button"
                                onClick={removeProfile}
                                className="eyebrow link-underline text-red-800"
                            >
                                Remove profile
                            </button>
                        )}
                    </div>
                </Group>

                <Group title="Identity">
                    <Field
                        label="Legal name"
                        hint="The name this identity trades under legally. Printed at the foot of the invoice."
                    >
                        <input
                            className="editorial-input"
                            value={form.legal_name}
                            onChange={(e) => set("legal_name", e.target.value)}
                            data-testid="seller-legal-name"
                        />
                    </Field>
                    <Field
                        label="Trading name"
                        hint="What appears as the wordmark at the top of the invoice, and in the link preview when you share it."
                    >
                        <input
                            className="editorial-input"
                            value={form.trading_name}
                            onChange={(e) => set("trading_name", e.target.value)}
                        />
                    </Field>
                    <Field label="Address" hint="Line breaks are kept as typed.">
                        <textarea
                            rows={3}
                            className="editorial-input resize-none"
                            value={form.address}
                            onChange={(e) => set("address", e.target.value)}
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-6">
                        <Field label="Email">
                            <input
                                className="editorial-input"
                                value={form.email}
                                onChange={(e) => set("email", e.target.value)}
                            />
                        </Field>
                        <Field label="Phone">
                            <input
                                className="editorial-input"
                                value={form.phone}
                                onChange={(e) => set("phone", e.target.value)}
                            />
                        </Field>
                    </div>
                    <Field label="Website">
                        <input
                            className="editorial-input"
                            value={form.website}
                            onChange={(e) => set("website", e.target.value)}
                        />
                    </Field>
                    <Field
                        label="Link preview card"
                        hint="The 1200×630 image shown when you paste an invoice link into a chat. Leave blank and the preview shows the name and description with no picture — which is what you want until this identity has a card of its own, because /invoice-card.png reads “Invoice — Depmont”."
                    >
                        <input
                            className="editorial-input"
                            value={form.share_card}
                            onChange={(e) => set("share_card", e.target.value)}
                            placeholder="Blank, or /invoice-card.png"
                            data-testid="profile-card"
                        />
                    </Field>
                </Group>

                <Group title="Tax">
                    <Field
                        label="GST registered"
                        hint="Set per profile. Leave as No until registration actually completes — while it is No, invoices from this identity state plainly that no GST has been charged, which is the correct disclosure rather than an omission."
                    >
                        <select
                            className="editorial-input"
                            value={form.gst_registered ? "yes" : "no"}
                            onChange={(e) => set("gst_registered", e.target.value === "yes")}
                            data-testid="seller-gst-registered"
                        >
                            <option value="no">No — not registered</option>
                            <option value="yes">Yes — registered</option>
                        </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-6">
                        <Field label="GSTIN" hint="Only once registered.">
                            <input
                                className="editorial-input"
                                value={form.gstin}
                                onChange={(e) => set("gstin", e.target.value.toUpperCase())}
                            />
                        </Field>
                        <Field label="PAN">
                            <input
                                className="editorial-input"
                                value={form.pan}
                                onChange={(e) => set("pan", e.target.value.toUpperCase())}
                            />
                        </Field>
                    </div>
                </Group>

                <Group title="How clients pay">
                    <p className="font-sans-tight text-xs text-[#1B1B1B]/55 font-light leading-[1.6] -mt-2">
                        At least one of UPI or a bank account is required before this
                        profile can issue anything. These appear on unpaid invoices only —
                        once an invoice is marked paid the page stops displaying them.
                        Whatever you put here must match the account that actually
                        receives the money, or the client's transfer may be queried.
                    </p>
                    <div className="grid grid-cols-2 gap-6">
                        <Field label="UPI ID">
                            <input
                                className="editorial-input"
                                value={form.upi_id}
                                onChange={(e) => set("upi_id", e.target.value)}
                                placeholder="name@bank"
                                data-testid="seller-upi"
                            />
                        </Field>
                        <Field
                            label="UPI display name"
                            hint="The name the client will see confirming the payee."
                        >
                            <input
                                className="editorial-input"
                                value={form.upi_name}
                                onChange={(e) => set("upi_name", e.target.value)}
                            />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <Field label="Account holder name">
                            <input
                                className="editorial-input"
                                value={form.bank_account_name}
                                onChange={(e) => set("bank_account_name", e.target.value)}
                            />
                        </Field>
                        <Field label="Account number">
                            <input
                                className="editorial-input"
                                value={form.bank_account_number}
                                onChange={(e) => set("bank_account_number", e.target.value)}
                                data-testid="seller-account"
                            />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <Field label="IFSC">
                            <input
                                className="editorial-input"
                                value={form.bank_ifsc}
                                onChange={(e) => set("bank_ifsc", e.target.value.toUpperCase())}
                            />
                        </Field>
                        <Field label="Bank name">
                            <input
                                className="editorial-input"
                                value={form.bank_name}
                                onChange={(e) => set("bank_name", e.target.value)}
                            />
                        </Field>
                    </div>
                    <Field label="Branch">
                        <input
                            className="editorial-input"
                            value={form.bank_branch}
                            onChange={(e) => set("bank_branch", e.target.value)}
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-6">
                        <Field
                            label="SWIFT / BIC"
                            hint="Needed only for payments from outside India."
                        >
                            <input
                                className="editorial-input"
                                value={form.bank_swift}
                                onChange={(e) => set("bank_swift", e.target.value.toUpperCase())}
                            />
                        </Field>
                        <Field label="IBAN" hint="Also international only. Leave blank otherwise.">
                            <input
                                className="editorial-input"
                                value={form.bank_iban}
                                onChange={(e) => set("bank_iban", e.target.value.toUpperCase())}
                            />
                        </Field>
                    </div>
                </Group>

                <Group title="Default wording">
                    <Field
                        label="Payment terms"
                        hint="Pre-filled on each new invoice from this profile. You can change it per invoice."
                    >
                        <textarea
                            rows={3}
                            className="editorial-input resize-none"
                            value={form.default_terms}
                            onChange={(e) => set("default_terms", e.target.value)}
                        />
                    </Field>
                    <Field
                        label="Notes"
                        hint="Optional. Leave empty unless something should appear on every invoice."
                    >
                        <textarea
                            rows={2}
                            className="editorial-input resize-none"
                            value={form.default_notes}
                            onChange={(e) => set("default_notes", e.target.value)}
                        />
                    </Field>
                </Group>
            </div>

            <div className="mt-10 pt-6 border-t border-[#E6E2D6] max-w-2xl flex items-center justify-between gap-6">
                <p className="eyebrow text-[#1B1B1B]/60">{status}</p>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="seller-save"
                    className="inline-flex items-center gap-3 px-7 py-3 bg-[#1B1B1B] text-[#F6F3EC] font-sans-tight text-[12px] tracking-[0.2em] uppercase hover:bg-[#A8844F] transition-colors duration-500 disabled:opacity-60"
                >
                    {saving ? "Saving…" : "Save"}
                </button>
            </div>
        </section>
    );
}

const Group = ({ title, children }) => (
    <div>
        <p className="eyebrow text-[#A8844F] mb-5">{title}</p>
        <div className="space-y-6">{children}</div>
    </div>
);

const Field = ({ label, hint, children }) => (
    <div>
        <label className="eyebrow text-[#1B1B1B]/60">{label}</label>
        {children}
        {hint && (
            <p className="mt-2 font-sans-tight text-xs text-[#1B1B1B]/55 leading-[1.5] max-w-md font-light">
                {hint}
            </p>
        )}
    </div>
);
